import { useEffect, useRef, useSyncExternalStore } from "react";
import { useLocation, useSearch } from "wouter";
import { AttributionRelay } from "@/lib/analytics/AttributionRelay";
import { trackPageView } from "@/lib/analytics/events";
import {
  getMarketingIdentityGeneration,
  getMarketingSessionId,
  subscribeMarketingIdentityBoundaries,
} from "@/lib/analytics/marketingSession";
import { getAnalyticsLocation, getGoogleAnalyticsLocation } from "@/lib/analytics/urlSanitization";

/**
 * Tracks a canonical page_view on the initial document and every SPA location change. The nested
 * attribution relay's effect runs first, so a paid-landing capture key is persisted before the
 * page-view envelope snapshots it.
 */
export const PageViewTracker = () => {
  const [location] = useLocation();
  const search = useSearch();
  const identityGeneration = useSyncExternalStore(
    subscribeMarketingIdentityBoundaries,
    getMarketingIdentityGeneration,
    getMarketingIdentityGeneration,
  );
  const previousPageLocation = useRef<string | undefined>(undefined);
  const lastTrackedPage = useRef<{ journeyId: string; dedupeKey: string } | undefined>(undefined);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const currentPageLocation = getAnalyticsLocation(window.location.href, window.location.origin)?.url;
    if (!currentPageLocation) return;
    const journeyId = getMarketingSessionId();
    const previousTrackedPage = lastTrackedPage.current;
    const crossedJourneyBoundary = Boolean(previousTrackedPage && previousTrackedPage.journeyId !== journeyId);

    // Dedupe on the actual pathname plus the sanitized location the event will carry (never on
    // raw search): a query-only application-state change (filters, pagination, provider callback
    // params being cleaned up) sanitizes to the same page_location and must not re-emit an
    // identical page_view — while navigating between two entities under the same redacted route
    // template (/campaign/A → /campaign/B, both emitted as /campaign/:id) is a genuine new view
    // and must emit again. The raw pathname stays in document memory only.
    const emittedLocation = crossedJourneyBoundary
      ? currentPageLocation
      : getGoogleAnalyticsLocation(window.location.href, window.location.origin);
    if (!emittedLocation) return;
    const dedupeKey = `${window.location.pathname}|${emittedLocation}`;
    if (previousTrackedPage?.journeyId === journeyId && previousTrackedPage.dedupeKey === dedupeKey) {
      return;
    }

    trackPageView({
      // Never carry attribution still present in the address bar across an owner boundary.
      url: crossedJourneyBoundary ? currentPageLocation : window.location.href,
      referrer: crossedJourneyBoundary ? undefined : (previousPageLocation.current ?? document.referrer),
      siteOrigin: window.location.origin,
    });
    previousPageLocation.current = currentPageLocation;
    lastTrackedPage.current = { journeyId, dedupeKey };
  }, [identityGeneration, location, search]);

  return <AttributionRelay />;
};
